import type { CrudFieldOption } from '@open-mercato/ui/backend/CrudForm'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import type { ComboboxOption } from '@open-mercato/ui/backend/inputs'

const DEMO_LOCATION_CODES = ['POZ-FRA', 'GDN-BH', 'BLN-CDK', 'STR-CPH'] as const

type SlotLocationRecord = {
  id?: unknown
  name?: unknown
  code?: unknown
  type?: unknown
  address?: {
    city?: unknown
    country?: unknown
  } | null
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function formatLocationLabel(location: SlotLocationRecord): string | null {
  const id = asText(location.id)
  const name = asText(location.name)
  if (!id || !name) return null
  const code = asText(location.code)
  return code ? `${name} (${code})` : name
}

function formatLocationDescription(location: SlotLocationRecord): string | null {
  const city = asText(location.address?.city)
  const country = asText(location.address?.country)
  const type = asText(location.type)
  const parts = [city, country, type].filter((value): value is string => typeof value === 'string' && value.length > 0)
  return parts.length > 0 ? parts.join(' • ') : null
}

export async function loadSlotLocationComboboxOptions(query?: string): Promise<ComboboxOption[]> {
  const params = new URLSearchParams({
    pageSize: '20',
    slotBookingEnabled: 'true',
    isActive: 'true',
    sortField: 'name',
    sortDir: 'asc',
  })
  const search = asText(query)
  if (search) {
    params.set('search', search)
  }

  const payload = await readApiResultOrThrow<{ items?: SlotLocationRecord[] }>(
    `/api/freight/locations?${params.toString()}`,
  )

  const options = (payload.items ?? [])
    .map((location): ComboboxOption | null => {
      const value = asText(location.id)
      const label = formatLocationLabel(location)
      if (!value || !label) return null
      return {
        value,
        label,
        description: formatLocationDescription(location),
      }
    })
    .filter((option): option is ComboboxOption => option !== null)

  if (search) {
    return options
  }

  const priority = new Map<string, number>(DEMO_LOCATION_CODES.map((code, index) => [code, index]))
  return [...options].sort((left, right) => {
    const leftCodeMatch = left.label.match(/\(([^)]+)\)$/)
    const rightCodeMatch = right.label.match(/\(([^)]+)\)$/)
    const leftPriority = leftCodeMatch ? priority.get(leftCodeMatch[1] ?? '') : undefined
    const rightPriority = rightCodeMatch ? priority.get(rightCodeMatch[1] ?? '') : undefined

    if (leftPriority !== undefined && rightPriority !== undefined) return leftPriority - rightPriority
    if (leftPriority !== undefined) return -1
    if (rightPriority !== undefined) return 1
    return left.label.localeCompare(right.label)
  })
}

export async function loadSlotLocationFieldOptions(query?: string): Promise<CrudFieldOption[]> {
  const options = await loadSlotLocationComboboxOptions(query)
  return options.map((option) => ({
    value: option.value,
    label: option.description ? `${option.label} — ${option.description}` : option.label,
  }))
}
